import { useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";

import { OrderStatusOption, type Order } from "@fitness-app/data-models/entities/Order";

import { supabase } from "~/store/initializeStore";
import { useCheckoutVariants } from "./useCheckoutVariants";

export type CheckoutVariantOrder = Pick<
  Order,
  "id" | "status" | "price" | "priceId" | "createdAt" | "metadata" | "items" | "refund"
> & {
  checkoutVariantId: string | null;
  paymentGateway: string | null;
  paymentMethodType: string | null;
  clientEmail: string | null;
};

export const useCheckoutVariantOrders = (
  checkoutVariantIds: string[],
  productId: string,
  dateRange: [Dayjs, Dayjs] | null,
) => {
  const { data: checkoutVariants } = useCheckoutVariants(productId);
  const dateRangeKey = dateRange ? [dateRange[0].format("YYYY-MM-DD"), dateRange[1].format("YYYY-MM-DD")] : null;

  return useQuery(["checkoutVariantOrders", checkoutVariantIds, productId, dateRangeKey], {
    enabled: checkoutVariants && checkoutVariantIds.length > 0 && checkoutVariants?.length > 0,
    keepPreviousData: true,
    queryFn: async () => {
      if (!checkoutVariantIds.length || !checkoutVariants) {
        return [];
      }

      const variantSlugs =
        checkoutVariants.filter((variant) => checkoutVariantIds.includes(variant.id)).map((variant) => variant.slug) ??
        [];

      const formatInValues = (values: string[]) => values.map((value) => `"${value.replace(/"/g, '\\"')}"`).join(",");

      const checkoutVariantIdsFilter = `checkoutVariantId.in.(${formatInValues(checkoutVariantIds)})`;
      const variantSlugsFilter = `metadata->sessionMetadata->>variantSlug.in.(${formatInValues(variantSlugs)})`;

      let query = supabase
        .from("product_order")
        .select(
          "id,status,price,priceId,createdAt,checkoutVariantId,metadata,items,paymentGateway,paymentMethodType,refund,clientEmail",
        )
        .or(variantSlugs.length > 0 ? `${checkoutVariantIdsFilter},${variantSlugsFilter}` : checkoutVariantIdsFilter)
        // A fully refunded order was still a sale; dropping it would hide the refund it carries.
        .in("status", [OrderStatusOption.Paid, OrderStatusOption.Refunded]);

      if (dateRange) {
        query = query
          .gte("createdAt", dateRange[0].startOf("day").toISOString())
          .lte("createdAt", dateRange[1].endOf("day").toISOString());
      }

      const { data, error } = await query
        .order("createdAt", { ascending: true })
        .limit(3000)
        .returns<CheckoutVariantOrder[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data ?? [];
    },
  });
};
